import { type FC, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Upload, Eye, EyeOff, Loader2, ShieldAlert } from 'lucide-react';
import { getPublicInvitation } from '../api/invitations.js';
import { useAuthStore } from '../store/auth-store.js';
import { LegalLinks } from '../components/LegalLinks.js';

export const AcceptInvitation: FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const { acceptInvitation, logout, isLoading, error, clearError } = useAuthStore();

  const invitationQuery = useQuery({
    queryKey: ['public-invitation', token],
    queryFn: () => getPublicInvitation(token as string),
    enabled: token !== undefined,
    retry: 1,
  });

  const invitation = invitationQuery.data;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation) return;
    await logout();
    try {
      await acceptInvitation(token as string, { password, name: name.trim() !== '' ? name : undefined });
      navigate('/', { replace: true });
    } catch {
      // error is in store
    }
  };

  if (invitationQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  if (invitationQuery.isError || invitation === undefined || invitation.status !== 'PENDING') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-950">
        <div className="w-full max-w-sm space-y-4 px-4 text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-red-900/30">
            <ShieldAlert className="h-6 w-6 text-red-400" />
          </div>
          <h1 className="text-lg font-bold text-slate-100">Invitation unavailable</h1>
          <p className="text-sm text-slate-500">
            This invitation link is invalid, expired, or has already been used.
          </p>
          <Link
            to="/login"
            className="inline-flex h-9 items-center justify-center rounded-lg bg-blue-600 px-4 text-sm font-medium text-white transition-colors hover:bg-blue-500"
          >
            Go to Login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950">
      <div className="w-full max-w-sm space-y-6 px-4">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-blue-600">
            <Upload className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-bold text-slate-100">Join {invitation.organizationName}</h1>
          <p className="mt-1 text-sm text-slate-500">Content Uploader · Laxmi AI</p>
        </div>

        <div className="rounded-lg border border-slate-800 bg-slate-900/40 px-4 py-3 text-sm">
          <p className="text-slate-300">
            <span className="font-medium text-slate-100">{invitation.creatorName}</span> has been added as a
            creator under <span className="font-medium text-slate-100">{invitation.organizationName}</span>.
            Set a password to join.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="mb-1.5 block text-xs font-medium text-slate-400">Email</label>
            <input
              id="email"
              type="email"
              value={invitation.email}
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-300"
              disabled
            />
          </div>
          <div>
            <label htmlFor="name" className="mb-1.5 block text-xs font-medium text-slate-400">
              Display name (optional)
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); clearError(); }}
              className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 px-3 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder={invitation.creatorName}
            />
          </div>
          <div>
            <label htmlFor="password" className="mb-1.5 block text-xs font-medium text-slate-400">Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => { setPassword(e.target.value); clearError(); }}
                className="h-9 w-full rounded-lg border border-slate-700 bg-slate-800/50 pl-3 pr-9 text-sm text-slate-100 placeholder:text-slate-500 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="At least 8 characters"
                minLength={8}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error !== null && (
            <div className="rounded-lg border border-red-900/50 bg-red-900/10 px-3 py-2 text-xs text-red-400">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="flex h-9 w-full items-center justify-center rounded-lg bg-blue-600 text-sm font-medium text-white transition-colors hover:bg-blue-500 disabled:opacity-50"
          >
            {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Join Agency'}
          </button>
        </form>

        <LegalLinks action="accepting the invitation" />

        <p className="text-center text-xs text-slate-500">
          Already have an account?{' '}
          <Link to="/login" className="font-medium text-blue-400 hover:text-blue-300">
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
};